import { lazy, LazyExoticComponent } from "react";

/**
 * Marks that this tab has already reloaded to recover a missing chunk. Session-scoped on purpose:
 * the condition is a property of this document's build, and a new tab starts clean.
 */
export const CHUNK_RELOAD_KEY = "cfc_chunk_reload_attempted";

/**
 * A deploy deletes the previous build's hashed assets (`aws s3 sync --delete` in the shared
 * frontend pipeline). A tab loaded before the deploy still holds the old chunk names, so its next
 * `import()` asks for a file that no longer exists. CloudFront answers a missing `/assets/*` with
 * `index.html` at HTTP 200, the browser refuses to evaluate HTML as a module, and the router's
 * error boundary shows a dead page. Reloading picks up the current `index.html`; this module makes
 * that reload automatic. See AIM-2776.
 */

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module", // Chrome
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari
];

/**
 * Deliberately narrow. A module that fetched fine but threw while evaluating is an ordinary bug:
 * reloading would not fix it and would cost the user whatever they had typed. Anything
 * unrecognised is left to the error boundary.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

/**
 * Storage can throw outright (Safari private mode, blocked site data). A throwing read is treated
 * as "not yet reloaded": the worst case is a second reload rather than none.
 */
const hasAlreadyReloaded = (): boolean => {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
};

const markReloadAttempted = (): void => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
  } catch {
    // Without storage the guard cannot be armed; reloading anyway is the better failure mode.
  }
};

const clearReloadAttempted = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Ignored for the same reason as above.
  }
};

/** A background tab reloading unseen costs a full boot the user never asked for; wait until they look. */
const reloadWhenVisible = (): void => {
  if (document.visibilityState !== "hidden") {
    window.location.reload();
    return;
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      return;
    }
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.location.reload();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
};

/**
 * Reloads the document once per session for a chunk that cannot be fetched.
 *
 * @returns whether a reload was triggered. `false` means the caller should let the error surface:
 * either it is not a chunk failure, or the one reload is spent and the chunk is missing from the
 * current build too. Reloading again would loop forever.
 */
export const recoverFromChunkLoadError = (error: unknown): boolean => {
  if (!isChunkLoadError(error) || hasAlreadyReloaded()) {
    return false;
  }

  markReloadAttempted();
  reloadWhenVisible();

  return true;
};

type ModuleFactory<T> = () => Promise<{ default: T }>;

/**
 * Wraps a dynamic-import factory with one retry (for transient network failures) and then the
 * guarded reload.
 *
 * On success the guard is cleared, which re-arms recovery for the next deploy.
 */
export const withChunkReload = <T>(factory: ModuleFactory<T>): ModuleFactory<T> => {
  return async () => {
    try {
      const module = await factory();
      clearReloadAttempted();

      return module;
    } catch (error) {
      if (!isChunkLoadError(error)) {
        throw error;
      }

      try {
        const module = await factory();
        clearReloadAttempted();

        return module;
      } catch (retryError) {
        if (!recoverFromChunkLoadError(retryError)) {
          throw retryError;
        }

        // Never settling keeps the error boundary from painting a dead page over the reload.
        return new Promise<{ default: T }>(() => {});
      }
    }
  };
};

/**
 * Borrowed from `React.lazy`'s own signature: writing it out means `ComponentType<any>`, which
 * `no-explicit-any` rejects, and `ComponentType<never>` stops real components from satisfying it.
 */
type LazyComponent = Parameters<typeof lazy>[0] extends () => Promise<{ default: infer C }> ? C : never;

export const lazyWithChunkReload = <T extends LazyComponent>(factory: ModuleFactory<T>): LazyExoticComponent<T> =>
  lazy(withChunkReload(factory));

type PreloadErrorEvent = Event & { payload?: unknown };

/**
 * Catches the failures that never reach a `lazy()` factory: Vite fires `vite:preloadError` when a
 * preload cannot fetch a chunk, and rethrows unless the event is cancelled.
 *
 * Call once from the entry module, before React mounts.
 */
export const initChunkLoadRecovery = (): void => {
  window.addEventListener("vite:preloadError", (event: Event) => {
    const { payload } = event as PreloadErrorEvent;

    if (recoverFromChunkLoadError(payload ?? new Error("Importing a module script failed"))) {
      event.preventDefault();
    }
  });
};
